import { mulberry32 } from './rng';

/** 视口尺寸。 */
export interface Viewport {
  width: number;
  height: number;
}

/** 板与托盘的整体布局结果（canvas 世界坐标）。 */
export interface BoardLayout {
  boardX: number;
  boardY: number;
  boardW: number;
  boardH: number;
  trayX: number;
  trayY: number;
  trayW: number;
  trayH: number;
  /** 相对原图的缩放比（≤1）。 */
  scale: number;
}

export interface LayoutOptions {
  trayWidth?: number;
  margin?: number;
  reservedTop?: number; // 工具栏预留高度
}

/**
 * 计算板与托盘布局：板居中（避开右侧托盘），等比缩放到可用区，不放大。
 */
export function computeLayout(
  viewport: Viewport,
  imageW: number,
  imageH: number,
  opts: LayoutOptions = {},
): BoardLayout {
  const margin = opts.margin ?? 16;
  const reservedTop = opts.reservedTop ?? 56;

  // 窄屏：托盘在底部（水平），板上居中；为底部 gallery 控件条预留高度
  if (viewport.width < 768) {
    const trayHeight = 200;
    const galleryH = 82; // 底部 ➕/🎲 控件条高度
    const availW = Math.max(0, viewport.width - margin * 2);
    const availH = Math.max(0, viewport.height - reservedTop - trayHeight - galleryH - margin * 2);
    const scale = Math.min(1, availW / imageW, availH / imageH);
    const boardW = imageW * scale;
    const boardH = imageH * scale;
    return {
      boardX: margin + (availW - boardW) / 2,
      boardY: reservedTop + (availH - boardH) / 2,
      boardW,
      boardH,
      trayX: margin,
      trayY: viewport.height - trayHeight - galleryH - margin,
      trayW: viewport.width - margin * 2,
      trayH: trayHeight,
      scale,
    };
  }

  // 宽屏：托盘在右侧
  const trayWidth = opts.trayWidth ?? 300;
  const availW = Math.max(0, viewport.width - trayWidth - margin * 2);
  const availH = Math.max(0, viewport.height - reservedTop - margin * 2);
  const scale = Math.min(1, availW / imageW, availH / imageH);
  const boardW = imageW * scale;
  const boardH = imageH * scale;
  const boardX = margin + (availW - boardW) / 2;
  const boardY = reservedTop + (availH - boardH) / 2;
  return {
    boardX,
    boardY,
    boardW,
    boardH,
    trayX: viewport.width - trayWidth - margin,
    trayY: reservedTop,
    trayW: trayWidth,
    trayH: viewport.height - reservedTop - margin,
    scale,
  };
}

export interface ScatterArgs {
  count: number;
  trayX: number;
  trayY: number;
  trayW: number;
  trayH: number;
  pieceW: number;
  pieceH: number;
  pad: number;
  rotateMode: boolean;
  rng: () => number;
}

export interface ScatterResult {
  x: number;
  y: number;
  rotation: number;
}

/**
 * 在托盘内散布碎片：按 cell 网格排列，每块在 cell 内轻微抖动；
 * rotateMode 开启时随机 90° 倍数旋转。
 * 返回每块的 {主体左上角 x, y, rotation}。
 */
export function scatterPieces(args: ScatterArgs): ScatterResult[] {
  const cellW = args.pieceW + args.pad * 2;
  const cellH = args.pieceH + args.pad * 2;
  const cols = Math.max(1, Math.floor(args.trayW / cellW));
  const rows = Math.ceil(args.count / cols);

  const rotations = [0, 90, 180, 270];
  const result: ScatterResult[] = [];
  let i = 0;
  for (let r = 0; r < rows && i < args.count; r++) {
    for (let c = 0; c < cols && i < args.count; c++, i++) {
      const cellX = args.trayX + c * cellW;
      const cellY = args.trayY + r * cellH;
      const slackX = Math.max(0, cellW - args.pieceW);
      const slackY = Math.max(0, cellH - args.pieceH);
      const jitterX = (args.rng() - 0.5) * slackX * 0.4;
      const jitterY = (args.rng() - 0.5) * slackY * 0.4;
      const x = cellX + slackX / 2 + jitterX;
      const y = cellY + slackY / 2 + jitterY;
      const rotation = args.rotateMode ? rotations[Math.floor(args.rng() * 4)] : 0;
      result.push({ x, y, rotation });
    }
  }
  // 随机打乱位置分配，避免碎片按 id 顺序排列
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(args.rng() * (i + 1));
    const tmp = result[i];
    result[i] = result[j];
    result[j] = tmp;
  }
  return result;
}

/** 便捷工厂：给定 seed 生成散布结果。 */
export function scatterWithSeed(
  args: Omit<ScatterArgs, 'rng'>,
  seed: number,
): ScatterResult[] {
  return scatterPieces({ ...args, rng: mulberry32(seed) });
}
